import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/de';
import { useNavigate } from 'react-router-dom';
import {
  FiCalendar,
  FiChevronLeft,
  FiChevronRight,
  FiMapPin,
  FiPercent,
} from 'react-icons/fi';
import './GuestSpots.css';

moment.locale('de');

const DAY_CAPACITY_MINUTES = 360;

const normalizeStatus = (status) => {
  if (
    status === 'green' ||
    status === 'yellow' ||
    status === 'red'
  ) {
    return status;
  }

  return 'red';
};

const normalizeDuration = (duration) => {
  const parsedDuration = Number(duration);

  if (!Number.isFinite(parsedDuration) || parsedDuration <= 0) {
    return 0;
  }

  return parsedDuration;
};

const getDayOccupancyPercent = (day) => {
  const bookedMinutes = Math.min(
    normalizeDuration(day.totalDuration),
    DAY_CAPACITY_MINUTES
  );

  return Math.round(
    (bookedMinutes / DAY_CAPACITY_MINUTES) * 100
  );
};

const getOccupancyPercent = (days) => {
  if (!days.length) return 0;

  const bookedMinutes = days.reduce((sum, day) => {
    return (
      sum +
      Math.min(
        normalizeDuration(day.totalDuration),
        DAY_CAPACITY_MINUTES
      )
    );
  }, 0);

  const availableMinutes =
    days.length * DAY_CAPACITY_MINUTES;

  return Math.round(
    (bookedMinutes / availableMinutes) * 100
  );
};

const getPercentTone = (percent) => {
  if (percent >= 70) return 'green';
  if (percent >= 35) return 'yellow';
  return 'red';
};

const formatMinutes = (minutes) => {
  const safeMinutes = normalizeDuration(minutes);
  const hours = Math.floor(safeMinutes / 60);
  const remainingMinutes = safeMinutes % 60;

  if (hours && remainingMinutes) {
    return `${hours} Std. ${remainingMinutes} Min.`;
  }

  if (hours) {
    return `${hours} Std.`;
  }

  return `${remainingMinutes} Min.`;
};


function GuestSpots({ selectedLocationId, locations = [] }) {
  const navigate = useNavigate();
  const currentMonth = useMemo(() => moment().startOf('month'), []);

  const [windowStart, setWindowStart] = useState(currentMonth.clone());
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!selectedLocationId) return;

    let cancelled = false;

    const loadGuestSpots = async () => {
      try {
        setLoading(true);
        setError('');

        const [availableResponse, statusResponse] = await Promise.all([
          axios.get('/api/available_days_with_artists', {
            params: { locationId: selectedLocationId },
          }),
          axios.get('/api/booking-status'),
        ]);

        if (cancelled) return;

        const statusMap = new Map(
          (statusResponse.data || []).map((status) => [
            `${status.artist_id}-${moment(status.day).format('YYYY-MM-DD')}`,
            {
              bookingStatus: normalizeStatus(
                status.bookingStatus
              ),
              totalDuration: normalizeDuration(
                status.totalDuration
              ),
            },
          ])
        );

        const mergedDays = (availableResponse.data || []).map((day) => {
          const dateKey = moment(day.day).format('YYYY-MM-DD');

          const statusData = statusMap.get(
            `${day.artistId}-${dateKey}`
          );

          const totalDuration =
            statusData?.totalDuration || 0;

          return {
            ...day,
            dateKey,
            bookingStatus:
              statusData?.bookingStatus || 'red',
            totalDuration,
            occupancyPercent: Math.round(
              (
                Math.min(
                  totalDuration,
                  DAY_CAPACITY_MINUTES
                ) /
                DAY_CAPACITY_MINUTES
              ) *
              100
            ),
          };
        });

        setDays(mergedDays);
      } catch (err) {
        console.error('Guest Spots konnten nicht geladen werden:', err);

        if (!cancelled) {
          setDays([]);
          setError('Guest Spots konnten nicht geladen werden.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadGuestSpots();

    return () => {
      cancelled = true;
    };
  }, [selectedLocationId]);

  const months = useMemo(() => {
    return Array.from({ length: 12 }, (_, index) => {
      const monthDate = windowStart.clone().add(index, 'month');
      const monthKey = monthDate.format('YYYY-MM');

      const monthDays = days.filter(
        (day) => moment(day.day).format('YYYY-MM') === monthKey
      );

      const artistsMap = monthDays.reduce((accumulator, day) => {
        const artistKey = String(day.artistId);

        if (!accumulator[artistKey]) {
          accumulator[artistKey] = {
            artistId: day.artistId,
            artistName: day.artistName || 'Unbekannt',
            days: [],
          };
        }

        accumulator[artistKey].days.push(day);
        return accumulator;
      }, {});

      const artists = Object.values(artistsMap)
        .map((artist) => {
          const sortedDays = [...artist.days].sort(
            (a, b) => moment(a.day).valueOf() - moment(b.day).valueOf()
          );

          return {
            ...artist,
            days: sortedDays,
            percent: getOccupancyPercent(sortedDays),
          };
        })
        .sort((a, b) => a.artistName.localeCompare(b.artistName, 'de'));

      return {
        monthKey,
        monthName: monthDate.format('MMMM'),
        year: monthDate.year(),
        artists,
        percent: getOccupancyPercent(monthDays),
      };
    });
  }, [days, windowStart]);

  const selectedLocation = locations.find(
    (item) => String(item.id) === String(selectedLocationId)
  );

  const rangeLabel = `${windowStart.format('MMMM YYYY')} – ${windowStart
    .clone()
    .add(11, 'months')
    .format('MMMM YYYY')}`;

  const isCurrentWindow = windowStart.isSame(currentMonth, 'month');

  const shiftWindow = (monthsToMove) => {
    setWindowStart((previous) =>
      previous.clone().add(monthsToMove, 'months').startOf('month')
    );
  };

  const returnToCurrentMonth = () => {
    setWindowStart(currentMonth.clone());
  };

  const openDay = (day) => {
    navigate('/', {
      state: {
        targetLocationId: selectedLocationId,
        highlightAppointment: {
          dayId: day.dayId,
          appointmentIds: [],
          type: 'navigation',
        },
      },
    });
  };

  return (
    <main className="guest-spots-page">
      <header className="guest-spots-header">
        <div className="guest-spots-heading">
          <span className="guest-spots-eyebrow">Guest Artists</span>
          <h1>Guest Spot Übersicht</h1>
          <p>{rangeLabel}</p>
        </div>

        <div className="guest-spots-meta">
          <div className="guest-spots-location">
            <FiMapPin />
            <span>{selectedLocation?.name || 'Standort'}</span>
          </div>

          <div className="guest-spots-navigation" aria-label="Zeitraum wechseln">
            <button
              type="button"
              onClick={() => shiftWindow(-12)}
              title="12 Monate zurück"
              aria-label="12 Monate zurück"
            >
              <FiChevronLeft />
              <span>12 Monate</span>
            </button>

            <button
              type="button"
              className="guest-spots-today"
              onClick={returnToCurrentMonth}
              disabled={isCurrentWindow}
            >
              Heute
            </button>

            <button
              type="button"
              onClick={() => shiftWindow(12)}
              title="12 Monate vor"
              aria-label="12 Monate vor"
            >
              <span>12 Monate</span>
              <FiChevronRight />
            </button>
          </div>
        </div>
      </header>

      <div className="guest-spots-toolbar">
        <div className="guest-spots-legend" aria-label="Legende">
          <span>
            <i className="green" />
            gut gebucht
          </span>

          <span>
            <i className="yellow" />
            teilweise gebucht
          </span>

          <span>
            <i className="red" />
            nicht gebucht
          </span>
        </div>

        <span className="guest-spots-hint">Tag anklicken, um im Kalender zu öffnen</span>
      </div>

      {loading ? (
        <div className="guest-spots-state">Guest Spots werden geladen …</div>
      ) : error ? (
        <div className="guest-spots-state error">{error}</div>
      ) : (
        <section className="guest-spots-grid">
          {months.map((month) => (
            <article className="guest-month-card" key={month.monthKey}>
              <div className="guest-month-header">
                <div>
                  <h2>{month.monthName}</h2>
                  <span>{month.year}</span>
                </div>

                <div
                  className={`guest-month-percent ${getPercentTone(
                    month.percent
                  )}`}
                >
                  <strong>{month.percent}%</strong>
                  <span>belegt</span>
                </div>
              </div>

              {month.artists.length ? (
                <div className="guest-artist-list">
                  {month.artists.map((artist) => (
                    <div className="guest-artist-row" key={artist.artistId}>
                      <div className="guest-artist-topline">
                        <strong title={artist.artistName}>{artist.artistName}</strong>
                        <span
                          className={`guest-artist-percent ${getPercentTone(
                            artist.percent
                          )}`}
                        >
                          {artist.percent}%
                        </span>
                      </div>

                      <div className="guest-day-list">
                        {artist.days.map((day) => (
                          <button
                            key={day.dayId}
                            type="button"
                            className={`guest-day ${day.bookingStatus}`}
                            onClick={() => openDay(day)}
                            title={`${moment(day.day).format(
                              'dddd, DD.MM.YYYY'
                            )} · ${formatMinutes(
                              day.totalDuration
                            )} gebucht · ${getDayOccupancyPercent(
                              day
                            )}%`}
                          >
                            <span>{moment(day.day).format('D')}</span>
                            <i />
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="guest-month-empty">Keine Guest Spots</div>
              )}
            </article>
          ))}
        </section>
      )}

      <footer className="guest-spots-footer">
        <div>
          <FiCalendar />
          <span>
            <strong>Rollierende Übersicht</strong>
            Aktueller Monat plus die folgenden elf Monate.
          </span>
        </div>

        <div>
          <FiPercent />
          <span>
            <strong>Kalenderstatus übernommen</strong>
            Grün, Gelb und Rot entsprechen exakt deiner Booking-Logik.
          </span>
        </div>
      </footer>
    </main>
  );
}

export default GuestSpots;
